import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FlaskConical, Loader2 } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { DtpResultsSignIn } from '../../components/dtp/DtpResultsSignIn';
import { LabAbnormalSummary, LabStatusBadge } from '../../components/labs/LabResultTable';
import { LabResultSummary } from '../../types/labResults';
import { LabOrdersInProgressSection } from '../../components/dtp/LabOrderTracker';

/**
 * A patient's own lab results.
 *
 * Gated inline rather than by DtpProtectedRoute, matching DtpResultDetail: a
 * patient who bookmarks this page and comes back a week later — past the 2-hour
 * patient session — should be asked for a code here, not bounced into a shop.
 */
export function DtpResults() {
  const { physicianId } = useDtpParams<{ physicianId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useDtpAuthStore();

  const [results, setResults] = useState<LabResultSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dtpApi
      .getLabResults()
      .then(res => {
        if (res.success && res.data) {
          setResults(res.data);
          setError('');
        } else {
          setError(res.error || 'We could not load your results.');
        }
      })
      .catch(() => setError('We could not load your results.'))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isAuthenticated) {
    return <DtpResultsSignIn physicianId={physicianId!} onAuthenticated={load} />;
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--portal-accent)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Lab results</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Every report your clinic has shared, newest first.</p>

      <LabOrdersInProgressSection />

      {error && (
        <p className="mt-4 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">{error}</p>
      )}

      {!error && !results.length && (
        <div className="portal-card mt-8 p-10 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-[var(--portal-tint-3)]"><FlaskConical className="h-6 w-6" /></span>
          <p className="portal-display mt-4 text-xl font-semibold">
            You don't have any lab results yet.
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--portal-muted)]">
            Results appear here once the lab has finished processing your sample. Your
            clinic will also text you when they're ready.
          </p>
        </div>
      )}

      <div className="mt-8 space-y-3">
        {results.map(result => (
          <button
            key={result.id}
            onClick={() => navigate(dtpPath(physicianId, `/results/${result.id}`))}
            className="portal-card group flex w-full items-center justify-between gap-4 p-5 text-left transition hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-28px_rgba(36,26,21,0.5)] motion-reduce:hover:translate-y-0"
          >
            <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--portal-tint-3)]"><FlaskConical className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="portal-display text-lg font-semibold">{result.panelName}</span>
                <LabStatusBadge status={result.status} amended={result.amended} />
              </div>
              <div className="mt-1.5">
                <LabAbnormalSummary
                  total={result.observationCount}
                  abnormal={result.abnormalCount}
                />
              </div>
              <p className="mt-1 text-xs text-[var(--portal-muted)]">{formatDate(result)}</p>
            </div>
            <ChevronRight aria-hidden="true" className="h-5 w-5 flex-shrink-0 text-[var(--portal-muted)] transition group-hover:translate-x-0.5" />
          </button>
        ))}
      </div>
    </div>
  );
}

function formatDate(result: { reportedAt?: string; collectedAt?: string; receivedAt?: string }): string {
  const raw = result.reportedAt || result.collectedAt || result.receivedAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export default DtpResults;
